// ════════════════════════════════════════════════════════════════════════
//  „zuletzt geübt“ – eine Rechnung, keine Formatierung
//
//  Die Lehrkraft fragt nicht nach dem 27. August, sondern danach, ob dieses
//  Kind noch dabei ist. Deshalb „gestern“ und „vor 3 Tagen“ – und ab vier
//  Wochen doch das Datum, weil beim Blick ins vergangene Schuljahr wieder die
//  Genauigkeit zählt.
//
//  Gerechnet wird über TAGESGRENZEN in Europe/Berlin, nicht über Stunden.
//  Postgres liefert UTC; eine Übung von gestern Abend hieße sonst am nächsten
//  Morgen „vor 14 Stunden“ oder, schlimmer, „heute“.
// ════════════════════════════════════════════════════════════════════════

const BERLIN = "Europe/Berlin";

const dayFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: BERLIN,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export const dateFormat = new Intl.DateTimeFormat("de-DE", {
  timeZone: BERLIN,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/**
 * Der Berliner Kalendertag als Zahl.
 *
 * Über den Kalendertag statt über Millisekunden: an den beiden
 * Umstellungswochenenden ist ein Tag 23 oder 25 Stunden lang, und eine Division
 * durch 86400000 zählt dort einen Tag zu viel oder zu wenig.
 */
function berlinDay(date: Date): number {
  const [year, month, day] = dayFormat.format(date).split("-").map(Number) as [
    number,
    number,
    number,
  ];
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

/**
 * Wie lange her – in Worten bis vier Wochen, danach als Datum.
 *
 * `now` ist ein Parameter und nicht `new Date()` im Rumpf: sonst ließe sich
 * kein Tageswechsel prüfen.
 */
export function lastPractised(iso: string | null, now: Date): string {
  if (iso === null) return "noch nicht";

  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "noch nicht";

  const days = berlinDay(now) - berlinDay(then);

  // Ein Wert aus der Zukunft ist keine Übung von morgen, sondern eine schiefe
  // Uhr auf einem Koffer-iPad. „heute“ ist die harmloseste Antwort darauf.
  if (days <= 0) return "heute";
  if (days === 1) return "gestern";
  if (days < 7) return `vor ${days} Tagen`;
  if (days < 28) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? "vor 1 Woche" : `vor ${weeks} Wochen`;
  }
  return dateFormat.format(then);
}

/** Das genaue Datum – steht immer zusätzlich im `title`. */
export function exactDate(iso: string | null): string | null {
  if (iso === null) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : dateFormat.format(date);
}
